import React, { useState, useRef } from 'react';
import { CheckCircle, Loader, ChevronRight, ChevronLeft, Upload } from 'lucide-react';

const inp = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400';

async function apiPost(url, body) {
  try {
    const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const d = await r.json();
    return { ok: r.ok, data: d };
  } catch (e) {
    return { ok: false, data: { message: 'Network error: ' + e.message } };
  }
}

export default function SetupWizard({ onComplete }) {
  const [step, setStep] = useState(1);
  const [dbTested, setDbTested] = useState(false);
  const [testing, setTesting] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [done, setDone] = useState(false);
  const [dbMsg, setDbMsg] = useState(null);
  const [adminErr, setAdminErr] = useState('');
  const [installErr, setInstallErr] = useState('');

  const [db, setDb] = useState({ host: '127.0.0.1', port: '3306', name: '', user: '', password: '' });
  const [school, setSchool] = useState('');
  const [adm, setAdm] = useState({ name: '', email: '', password: '', confirm: '' });

  // Branding
  const [logoPreview,  setLogoPreview]  = useState('');
  const [logoBase64,   setLogoBase64]   = useState('');
  const [logoExt,      setLogoExt]      = useState('');
  const [primaryColor,   setPrimaryColor]   = useState('#6b21a8');
  const [secondaryColor, setSecondaryColor] = useState('#9333ea');
  const logoInputRef = useRef(null);

  const setDbF = (k, v) => { setDb(d => ({ ...d, [k]: v })); setDbTested(false); setDbMsg(null); };
  const setA = (k, v) => setAdm(a => ({ ...a, [k]: v }));

  function handleLogoFile(file) {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target.result);
      const base64 = e.target.result.split(',')[1] || '';
      setLogoBase64(base64);
      setLogoExt(ext);
    };
    reader.readAsDataURL(file);
  }

  async function testDB() {
    if (!db.host || !db.name || !db.user) return setDbMsg({ err: 'Please fill in Host, DB Name and Username.' });
    setTesting(true); setDbMsg(null); setDbTested(false);
    const { ok, data } = await apiPost('/api/setup/test-db', db);
    if (data?.success) { setDbTested(true); setDbMsg({ ok: '✓ Connection successful!' }); }
    else setDbMsg({ err: '✗ ' + (data?.message || 'Connection failed') });
    setTesting(false);
  }

  function goStep2() {
    if (!db.host || !db.name || !db.user || !school) return setDbMsg({ err: 'Please fill in all fields including School Name.' });
    if (!dbTested) return setDbMsg({ err: 'Please test the DB connection first.' });
    setDbMsg(null); setStep(2);
  }

  async function install() {
    const { name, email, password, confirm } = adm;
    if (!name || !email || !password) return setAdminErr('Please fill in all fields.');
    if (password.length < 6) return setAdminErr('Password must be at least 6 characters.');
    if (password !== confirm) return setAdminErr('Passwords do not match.');
    setAdminErr(''); setInstallErr(''); setInstalling(true); setStep(3);
    const { ok, data } = await apiPost('/api/setup/install', {
      schoolName: school, db,
      admin: { name, email, password },
      logoBase64: logoBase64 || null,
      logoExt:    logoExt    || null,
      primaryColor,
      secondaryColor,
    });
    if (ok && data?.success) {
      setDone(true);
      setTimeout(() => onComplete(), 2000);
    } else {
      setInstalling(false); setStep(2);
      setInstallErr('Installation failed: ' + (data?.message || 'Unknown error'));
    }
  }

  const stepLabels = ['School Setup', 'Admin Account', 'Installing'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">

        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }} className="px-8 py-6 text-white text-center">
          {logoPreview
            ? <img src={logoPreview} alt="School Logo" style={{ maxHeight: '56px', width: 'auto', margin: '0 auto 10px' }} className="object-contain bg-white rounded-xl px-3 py-1" />
            : <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-white bg-opacity-20 flex items-center justify-center text-2xl font-bold">
                {school ? school[0].toUpperCase() : 'S'}
              </div>
          }
          <h1 className="text-xl font-bold">{school || 'School ERP'}</h1>
          <p className="text-sm mt-1 opacity-80">First-time Setup Wizard</p>
        </div>

        {/* Step indicators */}
        <div className="flex items-center px-8 py-4 border-b border-gray-100">
          {stepLabels.map((label, i) => {
            const s = i + 1;
            return (
              <React.Fragment key={s}>
                {i > 0 && <div className={`flex-1 h-0.5 ${step > s - 1 ? 'bg-green-400' : 'bg-gray-200'}`} />}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    step > s ? 'bg-green-500 text-white' : step === s ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-500'}`}>
                    {step > s ? '✓' : s}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${step === s ? 'text-gray-800' : 'text-gray-400'}`}>{label}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        <div className="p-8">

          {/* -- Step 1: School setup -- */}
          {step === 1 && (
            <div>
              <h2 className="text-lg font-bold mb-1">School Setup</h2>
              <p className="text-gray-500 text-sm mb-4">Configure your school details and database connection</p>

              {/* School Name */}
              <div className="mb-3">
                <label className="block text-xs font-semibold mb-1">School Name <span className="text-red-500">*</span></label>
                <input className={inp} value={school} onChange={e => setSchool(e.target.value)} placeholder="e.g. St. Mary's High School" />
              </div>

              {/* Logo + Colors */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-3">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Branding (optional)</p>

                {/* Logo upload */}
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center bg-white overflow-hidden flex-shrink-0">
                    {logoPreview
                      ? <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                      : <Upload size={18} className="text-gray-300" />
                    }
                  </div>
                  <div>
                    <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      className="hidden" onChange={e => handleLogoFile(e.target.files[0])} />
                    <button onClick={() => logoInputRef.current?.click()}
                      className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-100">
                      {logoPreview ? 'Change Logo' : 'Upload Logo'}
                    </button>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG or SVG recommended</p>
                  </div>
                </div>

                {/* Colors */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Primary Color</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer border border-gray-300 flex-shrink-0" />
                      <input type="text" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)}
                        maxLength={7} className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs font-mono focus:outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Secondary Color</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer border border-gray-300 flex-shrink-0" />
                      <input type="text" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)}
                        maxLength={7} className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs font-mono focus:outline-none" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Database */}
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Database Connection</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 text-xs text-blue-800 leading-relaxed">
                <strong>Hostinger:</strong> hPanel → Databases → MySQL Databases<br />
                Use Host: <strong>127.0.0.1</strong> &nbsp; Port: <strong>3306</strong>
              </div>

              {dbMsg?.err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-3 text-sm">{dbMsg.err}</div>}
              {dbMsg?.ok  && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 mb-3 text-sm">{dbMsg.ok}</div>}

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div><label className="block text-xs font-semibold mb-1">Host</label>
                  <input className={inp} value={db.host} onChange={e => setDbF('host', e.target.value)} /></div>
                <div><label className="block text-xs font-semibold mb-1">Port</label>
                  <input className={inp} value={db.port} onChange={e => setDbF('port', e.target.value)} /></div>
              </div>
              <div className="mb-3"><label className="block text-xs font-semibold mb-1">Database Name</label>
                <input className={inp} value={db.name} onChange={e => setDbF('name', e.target.value)} placeholder="e.g. u123456789_sms" />
              </div>
              <div className="mb-3"><label className="block text-xs font-semibold mb-1">Username</label>
                <input className={inp} value={db.user} onChange={e => setDbF('user', e.target.value)} placeholder="e.g. u123456789_admin" />
              </div>
              <div className="mb-5"><label className="block text-xs font-semibold mb-1">Password</label>
                <input className={inp} type="password" value={db.password} onChange={e => setDbF('password', e.target.value)} placeholder="DB password" />
              </div>

              <div className="flex gap-3">
                <button onClick={testDB} disabled={testing}
                  className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                  {testing ? 'Testing…' : 'Test Connection'}
                </button>
                <button onClick={goStep2}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-90"
                  style={{ backgroundColor: primaryColor }}>
                  Continue <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* -- Step 2: Admin account -- */}
          {step === 2 && (
            <div>
              <h2 className="text-lg font-bold mb-1">Create Admin Account</h2>
              <p className="text-gray-500 text-sm mb-4">This will be your login credentials for the system</p>

              {adminErr   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-3 text-sm">{adminErr}</div>}
              {installErr && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-3 text-sm whitespace-pre-wrap">{installErr}</div>}

              <div className="mb-3"><label className="block text-xs font-semibold mb-1">Full Name</label>
                <input className={inp} value={adm.name} onChange={e => setA('name', e.target.value)} placeholder="Your full name" />
              </div>
              <div className="mb-3"><label className="block text-xs font-semibold mb-1">Email</label>
                <input className={inp} type="email" value={adm.email} onChange={e => setA('email', e.target.value)} placeholder="admin@school.com" />
              </div>
              <div className="mb-3"><label className="block text-xs font-semibold mb-1">Password <span className="text-gray-400 font-normal">(min 6 chars)</span></label>
                <input className={inp} type="password" value={adm.password} onChange={e => setA('password', e.target.value)} placeholder="Choose a strong password" />
              </div>
              <div className="mb-5"><label className="block text-xs font-semibold mb-1">Confirm Password</label>
                <input className={inp} type="password" value={adm.confirm} onChange={e => setA('confirm', e.target.value)} placeholder="Re-enter password" />
              </div>

              <div className="flex gap-3">
                <button onClick={() => { setStep(1); setAdminErr(''); setInstallErr(''); }}
                  className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50">
                  <ChevronLeft size={16} /> Back
                </button>
                <button onClick={install}
                  className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-90"
                  style={{ backgroundColor: primaryColor }}>
                  Install Now
                </button>
              </div>
            </div>
          )}

          {/* -- Step 3: Installing / Done -- */}
          {step === 3 && (
            <div className="text-center py-6">
              {!done ? (
                <>
                  <Loader size={52} className="animate-spin mx-auto mb-4" style={{ color: primaryColor }} />
                  <h2 className="text-lg font-bold mb-1">Installing…</h2>
                  <p className="text-gray-500 text-sm">Creating database tables and admin account</p>
                </>
              ) : (
                <>
                  <CheckCircle size={52} className="text-green-500 mx-auto mb-4" />
                  <h2 className="text-lg font-bold mb-1">Setup Complete!</h2>
                  <p className="text-gray-500 text-sm">Loading login page…</p>
                </>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
